import type { ReactNode } from 'react';

import { Button } from '../../atoms/Button';
import { cn } from '../../lib/utils';

/**
 * Optional descriptor cluster rendered below the value: an 8px color square, a
 * label, and an optional secondary value.
 */
export interface MiniMetricDescriptor {
  /** CSS color (hex / rgb / hsl) for the 8px square. */
  dotColor: string;
  label: string;
  /** Optional secondary value rendered under the label, aligned with it. */
  subValue?: string | number;
}

/**
 * Optional footer "Show More"–style link rendered below the value/descriptor.
 * Either `onClick` or `href` may be supplied; when both are absent the link
 * still renders but does nothing.
 */
export interface MiniMetricFooterLink {
  label: string;
  /**
   * Optional purely-decorative suffix rendered after the label (e.g. a chevron
   * glyph). Marked `aria-hidden` so screen readers announce only `label` and
   * skip the visual symbol — keep the symbol out of `label` itself to avoid
   * announcements like "Show More logical or".
   */
  labelSuffix?: string;
  onClick?: () => void;
  href?: string;
  /** Extra classes merged onto the button/anchor element. */
  className?: string;
}

/**
 * `MiniMetricItem` absorbs the previously-considered `MetricSummaryCard`
 * pattern via the additive `descriptor` + `footerLink` slots — see TDEV-273
 * decision log #10 / canonicalization map § 9 for the merge rationale. A
 * separate `MetricSummaryCard` molecule is intentionally not shipped because
 * the additive slot approach covers every documented Figma frame.
 *
 * The component supports two layout modes:
 *
 * 1. **Value-first (canonical):** supply `value` (and optionally
 *    `changeIndicator`, `descriptor`, `footerLink`). The layout renders the
 *    label above the value, with optional descriptor/footerLink stacked
 *    below.
 * 2. **Legacy stateLabel:** supply `icon` + `stateLabel` (the historical
 *    Access Discovery shape). Retained for backward compatibility; new
 *    consumers should prefer the value-first API.
 */
export interface MiniMetricItemProps {
  /**
   * Optional label rendered above the value (value-first mode) or beside the
   * legacy state label cluster (legacy mode). Required in legacy mode by
   * convention; optional in value-first mode (TDEV-171 — Stale & Duplicated
   * tile may render a value with no upper label when the descriptor row
   * carries the title).
   */
  label?: string;
  /** NEW (value-first API): the metric value rendered prominently. */
  value?: string | number;
  /** NEW (value-first API): up/down arrow + magnitude rendered next to value. */
  changeIndicator?: { direction: 'up' | 'down'; magnitude: number };
  /** NEW: optional descriptor (8px square + label, sub-value under the label). */
  descriptor?: MiniMetricDescriptor;
  /** NEW: optional footer "Show More" link. */
  footerLink?: MiniMetricFooterLink;
  /**
   * Optional icon rendered to the left of the metric column.
   *
   * - In legacy `stateLabel` mode this same prop is rendered in a 24x24 left
   *   slot above the label (preserved bit-for-bit — the historical Access
   *   Discovery shape).
   * - In value-first mode (TDEV-171) the icon sits to the left of the entire
   *   label/value/descriptor/footerLink column. When omitted, no left slot is
   *   reserved.
   */
  icon?: ReactNode;
  /** LEGACY: state label rendered in place of `value` in the historical layout. */
  stateLabel?: string;
  className?: string;
}

export function MiniMetricItem({
  label,
  value,
  changeIndicator,
  descriptor,
  footerLink,
  icon,
  stateLabel,
  className,
}: MiniMetricItemProps): JSX.Element {
  // Legacy layout: when no `value` is supplied, fall back to the historical
  // icon + stateLabel + label cluster used by AccessDiscoveryBody.
  if (value === undefined) {
    return (
      <div className={cn('flex items-start gap-2', className)}>
        {icon !== undefined ? (
          <div className="flex size-6 shrink-0 items-center justify-center">{icon}</div>
        ) : null}
        <div className="flex flex-col gap-[6px]">
          {stateLabel !== undefined ? (
            <div className="flex h-[17px] items-center">
              <p className="tabular-nums text-[16px] font-medium leading-[1.2] text-dawn-black">
                {stateLabel}
              </p>
            </div>
          ) : null}
          <p className="text-[13px] font-normal leading-[1.3] text-dawn-black">{label}</p>
        </div>
      </div>
    );
  }

  // Value-first layout (canonical).
  return (
    <div className={cn('flex items-start gap-2', className)}>
      {icon !== undefined ? (
        <div className="flex size-6 shrink-0 items-center justify-center">{icon}</div>
      ) : null}
      <div className="flex flex-1 flex-col gap-1">
        {label !== undefined ? (
          <p className="text-[13px] font-normal leading-[1.3] text-dawn-black">{label}</p>
        ) : null}
        <div className="flex items-baseline gap-2">
          <p className="whitespace-nowrap tabular-nums text-[24px] font-medium leading-[1.2] text-dawn-black">
            {value}
          </p>
          {changeIndicator !== undefined ? (
            <span
              data-testid="mini-metric-change-indicator"
              className={cn(
                'text-[13px] font-medium',
                changeIndicator.direction === 'up' ? 'text-success' : 'text-error'
              )}
            >
              {changeIndicator.direction === 'up' ? '\u2191' : '\u2193'}
              {changeIndicator.magnitude}
            </span>
          ) : null}
        </div>
        {descriptor !== undefined ? (
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1">
            <span
              data-testid="mini-metric-descriptor-dot"
              className="inline-block size-2 rounded-[2px]"
              style={{ backgroundColor: descriptor.dotColor }}
              aria-hidden="true"
            />
            <span className="text-[13px] font-normal leading-[1.3] text-dawn-black">
              {descriptor.label}
            </span>
            {descriptor.subValue !== undefined ? (
              <span className="col-start-2 text-[13px] font-normal leading-[1.3] text-dawn-black">
                {descriptor.subValue}
              </span>
            ) : null}
          </div>
        ) : null}
        {footerLink !== undefined ? (
          // ShowMoreLink composition: children-after-icon, not Button trailingIcon
          // (see plan.md Phase 5 Task 5.5). Button only ships `leadingIcon`; the
          // trailing arrow is rendered as a sibling child node.
          <Button
            variant="link"
            size="sm"
            asChild={footerLink.href !== undefined}
            onClick={footerLink.onClick}
            className={cn('self-start px-0', footerLink.className)}
          >
            {footerLink.href !== undefined ? (
              <a href={footerLink.href}>
                {footerLink.label}
                {footerLink.labelSuffix !== undefined ? (
                  <span aria-hidden="true"> {footerLink.labelSuffix}</span>
                ) : null}
              </a>
            ) : (
              <>
                {footerLink.label}
                {footerLink.labelSuffix !== undefined ? (
                  <span aria-hidden="true"> {footerLink.labelSuffix}</span>
                ) : null}
              </>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

MiniMetricItem.displayName = 'MiniMetricItem';
